export interface NmapScript {
  id: string;
  output: string;
}

export interface NmapService {
  name: string;
  product: string;
  version: string;
  extrainfo: string;
  method: string;
  conf: string;
  tunnel: string;
}

export interface NmapPort {
  protocol: string;
  portid: string;
  state: string;
  reason: string;
  service: NmapService | null;
  scripts: NmapScript[];
}

export interface NmapExtraPorts {
  state: string;
  count: number;
}

export interface NmapOsMatch {
  name: string;
  accuracy: string;
  osclass: {
    type: string;
    vendor: string;
    osfamily: string;
    osgen: string;
  }[];
}

export interface NmapAddress {
  addr: string;
  addrtype: string;
  vendor: string;
}

export interface NmapHost {
  status: string;
  statusReason: string;
  addresses: NmapAddress[];
  hostnames: string[];
  ports: NmapPort[];
  extraPorts: NmapExtraPorts[];
  osMatches: NmapOsMatch[];
  hostScripts: NmapScript[];
  starttime: string;
  endtime: string;
  uptime: { seconds: string; lastboot: string } | null;
}

export interface NmapScanInfo {
  scanner: string;
  args: string;
  start: string;
  startstr: string;
  version: string;
  scanTypes: { type: string; protocol: string; numservices: string; services: string }[];
}

export interface NmapRunStats {
  elapsed: string;
  summary: string;
  exit: string;
  timestr: string;
  hostsUp: number;
  hostsDown: number;
  hostsTotal: number;
}

export interface NmapParseResult {
  scanInfo: NmapScanInfo;
  hosts: NmapHost[];
  runStats: NmapRunStats | null;
  warnings: string[];
}

function attr(el: Element, name: string): string {
  return el.getAttribute(name) ?? '';
}

function sanitizeXml(raw: string): string {
  let xml = raw.trim();

  // Strip XML declaration issues (partial/duplicate)
  xml = xml.replace(/<\?xml[^?]*\?>\s*/gi, '');
  // Strip DOCTYPE to avoid entity-resolution errors
  xml = xml.replace(/<!DOCTYPE[^>]*>/gi, '');

  // Re-add a clean XML declaration
  xml = '<?xml version="1.0" encoding="UTF-8"?>\n' + xml;

  // If the XML was truncated, close the most common open tags so DOMParser
  // can still produce a partial tree.
  const tagsToClose = ['script', 'port', 'ports', 'host', 'nmaprun'];
  for (const tag of tagsToClose) {
    const openRe = new RegExp(`<${tag}[\\s>/]`, 'gi');
    const closeRe = new RegExp(`</${tag}>`, 'gi');
    const opens = (xml.match(openRe) || []).length;
    const closes = (xml.match(closeRe) || []).length;
    for (let i = 0; i < opens - closes; i++) {
      xml += `</${tag}>`;
    }
  }

  return xml;
}

function parseHost(hostEl: Element): NmapHost {
  const statusEl = hostEl.querySelector('status');
  const status = statusEl ? attr(statusEl, 'state') : 'unknown';
  const statusReason = statusEl ? attr(statusEl, 'reason') : '';

  const addresses: NmapAddress[] = [];
  hostEl.querySelectorAll(':scope > address').forEach((addrEl) => {
    addresses.push({
      addr: attr(addrEl, 'addr'),
      addrtype: attr(addrEl, 'addrtype'),
      vendor: attr(addrEl, 'vendor'),
    });
  });

  const hostnames: string[] = [];
  hostEl.querySelectorAll('hostnames > hostname').forEach((hn) => {
    const name = attr(hn, 'name');
    if (name) hostnames.push(name);
  });

  const ports: NmapPort[] = [];
  hostEl.querySelectorAll('ports > port').forEach((portEl) => {
    const stateEl = portEl.querySelector('state');
    const serviceEl = portEl.querySelector('service');

    const scripts: NmapScript[] = [];
    portEl.querySelectorAll(':scope > script').forEach((scriptEl) => {
      scripts.push({ id: attr(scriptEl, 'id'), output: attr(scriptEl, 'output') });
    });

    ports.push({
      protocol: attr(portEl, 'protocol'),
      portid: attr(portEl, 'portid'),
      state: stateEl ? attr(stateEl, 'state') : 'unknown',
      reason: stateEl ? attr(stateEl, 'reason') : '',
      service: serviceEl
        ? {
            name: attr(serviceEl, 'name'),
            product: attr(serviceEl, 'product'),
            version: attr(serviceEl, 'version'),
            extrainfo: attr(serviceEl, 'extrainfo'),
            method: attr(serviceEl, 'method'),
            conf: attr(serviceEl, 'conf'),
            tunnel: attr(serviceEl, 'tunnel'),
          }
        : null,
      scripts,
    });
  });

  const extraPorts: NmapExtraPorts[] = [];
  hostEl.querySelectorAll('ports > extraports').forEach((ep) => {
    extraPorts.push({
      state: attr(ep, 'state'),
      count: parseInt(attr(ep, 'count'), 10) || 0,
    });
  });

  const osMatches: NmapOsMatch[] = [];
  hostEl.querySelectorAll('os > osmatch').forEach((om) => {
    const osclass: NmapOsMatch['osclass'] = [];
    om.querySelectorAll('osclass').forEach((oc) => {
      osclass.push({
        type: attr(oc, 'type'),
        vendor: attr(oc, 'vendor'),
        osfamily: attr(oc, 'osfamily'),
        osgen: attr(oc, 'osgen'),
      });
    });
    osMatches.push({ name: attr(om, 'name'), accuracy: attr(om, 'accuracy'), osclass });
  });

  const hostScripts: NmapScript[] = [];
  hostEl.querySelectorAll('hostscript > script').forEach((scriptEl) => {
    hostScripts.push({ id: attr(scriptEl, 'id'), output: attr(scriptEl, 'output') });
  });

  const uptimeEl = hostEl.querySelector('uptime');

  return {
    status,
    statusReason,
    addresses,
    hostnames,
    ports,
    extraPorts,
    osMatches,
    hostScripts,
    starttime: attr(hostEl, 'starttime'),
    endtime: attr(hostEl, 'endtime'),
    uptime: uptimeEl
      ? { seconds: attr(uptimeEl, 'seconds'), lastboot: attr(uptimeEl, 'lastboot') }
      : null,
  };
}

export function parseNmapXml(rawXml: string): NmapParseResult {
  const warnings: string[] = [];

  if (!rawXml.trim()) {
    return {
      scanInfo: { scanner: '', args: '', start: '', startstr: '', version: '', scanTypes: [] },
      hosts: [],
      runStats: null,
      warnings: ['Leerer Input'],
    };
  }

  const sanitized = sanitizeXml(rawXml);
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    warnings.push(
      'XML konnte nicht vollständig geparst werden (möglicherweise abgebrochen/unvollständig). ' +
        'Es werden so viele Daten wie möglich extrahiert.',
    );
  }

  const nmaprun = doc.querySelector('nmaprun');

  const scanInfo: NmapScanInfo = {
    scanner: nmaprun ? attr(nmaprun, 'scanner') : '',
    args: nmaprun ? attr(nmaprun, 'args') : '',
    start: nmaprun ? attr(nmaprun, 'start') : '',
    startstr: nmaprun ? attr(nmaprun, 'startstr') : '',
    version: nmaprun ? attr(nmaprun, 'version') : '',
    scanTypes: [],
  };

  (nmaprun ?? doc).querySelectorAll('scaninfo').forEach((si) => {
    scanInfo.scanTypes.push({
      type: attr(si, 'type'),
      protocol: attr(si, 'protocol'),
      numservices: attr(si, 'numservices'),
      services: attr(si, 'services'),
    });
  });

  const hosts: NmapHost[] = [];
  (nmaprun ?? doc).querySelectorAll('host').forEach((hostEl) => {
    try {
      hosts.push(parseHost(hostEl));
    } catch (e) {
      warnings.push(`Host konnte nicht geparst werden: ${e}`);
    }
  });

  let runStats: NmapRunStats | null = null;
  const finishedEl = (nmaprun ?? doc).querySelector('runstats > finished');
  const hostsEl = (nmaprun ?? doc).querySelector('runstats > hosts');
  if (finishedEl) {
    runStats = {
      elapsed: attr(finishedEl, 'elapsed'),
      summary: attr(finishedEl, 'summary'),
      exit: attr(finishedEl, 'exit'),
      timestr: attr(finishedEl, 'timestr'),
      hostsUp: parseInt(hostsEl ? attr(hostsEl, 'up') : '0', 10) || 0,
      hostsDown: parseInt(hostsEl ? attr(hostsEl, 'down') : '0', 10) || 0,
      hostsTotal: parseInt(hostsEl ? attr(hostsEl, 'total') : '0', 10) || 0,
    };
  }

  if (hosts.length === 0 && !parseError) {
    warnings.push('Keine Hosts im XML gefunden.');
  }

  return { scanInfo, hosts, runStats, warnings };
}

// ─── GNmap-style text output ──────────────────────────────────

function getIp(host: NmapHost): string {
  return host.addresses.find((a) => a.addrtype === 'ipv4')?.addr
    ?? host.addresses.find((a) => a.addrtype === 'ipv6')?.addr
    ?? host.addresses[0]?.addr
    ?? '?';
}

function getHostname(host: NmapHost): string {
  return host.hostnames[0] ?? '';
}

function buildServiceString(svc: NmapService | null): string {
  if (!svc) return '';
  const parts = [svc.product, svc.version, svc.extrainfo].filter(Boolean);
  return parts.join(' ');
}

export function toGnmapText(result: NmapParseResult): string {
  const lines: string[] = [];

  if (result.scanInfo.args) {
    lines.push(`# Nmap ${result.scanInfo.version} scan initiated ${result.scanInfo.startstr} as: ${result.scanInfo.args}`);
  }

  for (const host of result.hosts) {
    const ip = getIp(host);
    const hostname = getHostname(host);
    const hostLabel = hostname ? `${ip} (${hostname})` : ip;

    lines.push(`Host: ${hostLabel}\tStatus: ${host.status === 'up' ? 'Up' : 'Down'}`);

    const openPorts = host.ports.filter((p) => p.state !== 'closed');
    if (openPorts.length > 0) {
      const portStrings = openPorts.map((p) => {
        const svcStr = buildServiceString(p.service);
        return `${p.portid}/${p.state}/${p.protocol}//${p.service?.name ?? ''}//${svcStr}/`;
      });
      lines.push(`Host: ${ip}\tPorts: ${portStrings.join(', ')}`);
    }

    for (const ep of host.extraPorts) {
      lines.push(`Host: ${ip}\tIgnored State: ${ep.state} (${ep.count})`);
    }

    if (host.osMatches.length > 0) {
      const osStr = host.osMatches.map((m) => `${m.name} (${m.accuracy}%)`).join(', ');
      lines.push(`Host: ${ip}\tOS: ${osStr}`);
    }
  }

  if (result.runStats) {
    lines.push(`# ${result.runStats.summary || `Nmap done: ${result.runStats.hostsTotal} IP address(es) scanned in ${result.runStats.elapsed} seconds`}`);
  }

  return lines.join('\n');
}

// ─── Classic nmap text output (per-host) ──────────────────────

export function toNmapNormalText(result: NmapParseResult): string {
  const lines: string[] = [];

  if (result.scanInfo.args) {
    lines.push(`Starting Nmap ${result.scanInfo.version} ( https://nmap.org ) at ${result.scanInfo.startstr}`);
  }

  for (const host of result.hosts) {
    const ip = getIp(host);
    const hostname = getHostname(host);
    const reportLabel = hostname && hostname !== ip
      ? `${hostname} (${ip})`
      : ip;

    lines.push('');
    lines.push(`Nmap scan report for ${reportLabel}`);

    if (host.status === 'up') {
      const latency = host.statusReason ? ` (${host.statusReason})` : '';
      lines.push(`Host is up${latency}.`);
    } else {
      lines.push(`Host is down.`);
    }

    for (const ep of host.extraPorts) {
      lines.push(`Not shown: ${ep.count} ${ep.state} ports`);
    }

    if (host.ports.length > 0) {
      const portCol = 'PORT';
      const stateCol = 'STATE';
      const serviceCol = 'SERVICE';
      const versionCol = 'VERSION';

      const portStrs = host.ports.map((p) => `${p.portid}/${p.protocol}`);
      const stateStrs = host.ports.map((p) => p.state);
      const serviceStrs = host.ports.map((p) => {
        const svc = p.service;
        if (!svc) return '';
        return svc.tunnel ? `${svc.tunnel}/${svc.name}` : svc.name;
      });
      const versionStrs = host.ports.map((p) => buildServiceString(p.service));

      const portW = Math.max(portCol.length, ...portStrs.map((s) => s.length));
      const stateW = Math.max(stateCol.length, ...stateStrs.map((s) => s.length));
      const serviceW = Math.max(serviceCol.length, ...serviceStrs.map((s) => s.length));

      const pad = (s: string, w: number) => s + ' '.repeat(Math.max(0, w - s.length));

      lines.push(`${pad(portCol, portW)}  ${pad(stateCol, stateW)}  ${pad(serviceCol, serviceW)}  ${versionCol}`);

      host.ports.forEach((_, i) => {
        lines.push(
          `${pad(portStrs[i], portW)}  ${pad(stateStrs[i], stateW)}  ${pad(serviceStrs[i], serviceW)}  ${versionStrs[i]}`,
        );
      });
    }

    const macAddr = host.addresses.find((a) => a.addrtype === 'mac');
    if (macAddr) {
      lines.push(`MAC Address: ${macAddr.addr}${macAddr.vendor ? ` (${macAddr.vendor})` : ''}`);
    }

    if (host.osMatches.length > 0) {
      const osStr = host.osMatches
        .slice(0, 3)
        .map((m) => `${m.name} (${m.accuracy}%)`)
        .join(', ');
      lines.push(`OS details: ${osStr}`);
    }

    for (const s of host.hostScripts) {
      lines.push(`| ${s.id}:`);
      for (const line of s.output.split('\n')) {
        lines.push(`|   ${line}`);
      }
    }

    for (const p of host.ports) {
      for (const s of p.scripts) {
        lines.push(`| ${p.portid}/${p.protocol} ${s.id}:`);
        for (const line of s.output.split('\n')) {
          lines.push(`|   ${line}`);
        }
      }
    }
  }

  if (result.runStats) {
    lines.push('');
    lines.push(
      result.runStats.summary ||
        `Nmap done: ${result.runStats.hostsTotal} IP address(es) (${result.runStats.hostsUp} host(s) up) scanned in ${result.runStats.elapsed} seconds`,
    );
  }

  return lines.join('\n');
}

// ─── HTTP/HTTPS URL list ──────────────────────────────────────

const HTTP_SERVICE_NAMES = new Set([
  'http', 'https', 'http-alt', 'https-alt', 'http-proxy',
  'ssl/http', 'ssl/https',
]);

const HTTPS_PORTS = new Set(['443', '8443', '4443', '9443']);

function isHttpService(port: NmapPort): boolean {
  const svcName = port.service?.name ?? '';
  const tunnel = port.service?.tunnel ?? '';
  if (HTTP_SERVICE_NAMES.has(svcName)) return true;
  if (HTTP_SERVICE_NAMES.has(`${tunnel}/${svcName}`)) return true;
  if (svcName.startsWith('http')) return true;
  return false;
}

function isHttps(port: NmapPort): boolean {
  const svcName = port.service?.name ?? '';
  const tunnel = port.service?.tunnel ?? '';
  if (svcName === 'https' || svcName === 'https-alt') return true;
  if (tunnel === 'ssl') return true;
  if (HTTPS_PORTS.has(port.portid)) return true;
  return false;
}

export function toHttpUrls(result: NmapParseResult): string[] {
  const urls: string[] = [];

  for (const host of result.hosts) {
    if (host.status !== 'up') continue;

    const httpPorts = host.ports.filter((p) => p.state === 'open' && isHttpService(p));
    if (httpPorts.length === 0) continue;

    const hostname = getHostname(host);
    const ip = getIp(host);
    const target = hostname || ip;

    for (const p of httpPorts) {
      const ssl = isHttps(p);
      const scheme = ssl ? 'https' : 'http';
      const defaultPort = (scheme === 'http' && p.portid === '80') || (scheme === 'https' && p.portid === '443');
      const url = defaultPort
        ? `${scheme}://${target}`
        : `${scheme}://${target}:${p.portid}`;
      urls.push(url);
    }
  }

  return Array.from(new Set(urls));
}

export function getHostIp(host: NmapHost): string {
  return getIp(host);
}

export function getHostDisplayName(host: NmapHost): string {
  const ip = getIp(host);
  const hostname = getHostname(host);
  return hostname ? `${ip} (${hostname})` : ip;
}
